import { useContext, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import DocumentsChecklist from '../DocumentsChecklist';
import EmployeeService from '../../../../services/Employee';
import HireAgreementsService from '../../../../services/HireAgreements';
import {
  buildLockedKeys,
  buildPaperworkSelection,
  buildResendPaperworkPayload,
} from '../../../../helpers/paperworkSelection';

interface ResendPaperworkModalProps {
  isOpen: boolean;
  toggle: () => void;
  employee: any;
  onSuccess?: () => void;
}

const ResendPaperworkModal = ({ isOpen, toggle, employee, onSuccess }: ResendPaperworkModalProps) => {
  const { token }: any = useContext(AuthContext);
  const [hireAgreementsList, setHireAgreementsList] = useState<any[]>([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [lockedKeys, setLockedKeys] = useState<string[]>([]);
  const [employmentEligibilityDocs, setEmploymentEligibilityDocs] = useState<string[]>([]);
  const [hiringAgreements, setHiringAgreements] = useState<string[]>([]);
  const [taxDocs, setTaxDocs] = useState<string[]>([]);
  const [otherDocs, setOtherDocs] = useState<string[]>([]);
  const [i9Option, setI9Option] = useState('');
  const [onlyRequestedDocuments, setOnlyRequestedDocuments] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const seeded = buildPaperworkSelection(employee);
    setEmploymentEligibilityDocs(seeded.employmentEligibilityDocs);
    setHiringAgreements(seeded.hiringAgreements);
    setTaxDocs(seeded.taxDocs);
    setOtherDocs(seeded.otherDocs);
    setI9Option(seeded.i9Option);
    setLockedKeys(buildLockedKeys(employee));
    setReason('');
    setOnlyRequestedDocuments(employee?.status === 'ACTIVE');

    const loadAgreements = async () => {
      try {
        setLoadingAgreements(true);
        const response = await HireAgreementsService(token).list();
        setHireAgreementsList(response?.data || []);
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to load the hiring agreements.');
        setHireAgreementsList([]);
      } finally {
        setLoadingAgreements(false);
      }
    };

    loadAgreements();
  }, [isOpen, employee, token]);

  const changeEmploymentEligibility = (doc: string) => {
    const isChecked = employmentEligibilityDocs.includes(doc);
    setEmploymentEligibilityDocs(
      isChecked ? employmentEligibilityDocs.filter((value) => value !== doc) : [...employmentEligibilityDocs, doc],
    );
  };

  const changeTaxDocs = (doc: string) => {
    const isChecked = taxDocs.includes(doc);
    setTaxDocs(isChecked ? taxDocs.filter((value) => value !== doc) : [...taxDocs, doc]);
  };

  const changeOtherDocs = (doc: string) => {
    const isChecked = otherDocs.includes(doc);
    setOtherDocs(isChecked ? otherDocs.filter((value) => value !== doc) : [...otherDocs, doc]);
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    setSubmitting(true);
    try {
      const payload = buildResendPaperworkPayload(
        { employmentEligibilityDocs, hiringAgreements, taxDocs, otherDocs, i9Option },
        reason,
        onlyRequestedDocuments,
      );
      await EmployeeService(token).resendPaperwork(employee?.id, payload);
      toast.success('Sent successfully!');
      toggle();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to resend the paperwork email.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Paperwork</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <p className="text-muted mb-4">
            Are you sure you want to resend the paperwork email? The documents already requested are selected and cannot
            be removed. Select any additional document you want to request.
          </p>

          {loadingAgreements ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <DocumentsChecklist
              hireAgreementsList={hireAgreementsList}
              hiringAgreements={hiringAgreements}
              taxDocs={taxDocs}
              employmentEligibilityDocs={employmentEligibilityDocs}
              otherDocs={otherDocs}
              i9Option={i9Option}
              lockedKeys={lockedKeys}
              changeEmploymentEligibility={changeEmploymentEligibility}
              changeTaxDocs={changeTaxDocs}
              changeOtherDocs={changeOtherDocs}
              setHiringAgreements={setHiringAgreements}
              setI9Option={setI9Option}
            />
          )}

          <hr />

          <FormGroup check className="mb-3">
            <Label check>
              <Input
                type="checkbox"
                checked={onlyRequestedDocuments}
                onChange={(event) => setOnlyRequestedDocuments(event.target.checked)}
              />{' '}
              Open the link only on the documents listed above (skip the application and banking account steps)
            </Label>
          </FormGroup>

          <FormGroup className="form-control-label">
            <Label>Reason</Label>
            <Input
              type="textarea"
              name="reason"
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={toggle} disabled={submitting}>
            No
          </Button>
          <Button color="warning" type="submit" disabled={submitting || loadingAgreements}>
            {submitting ? 'Sending...' : 'Yes'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ResendPaperworkModal;
